﻿/*!
    *
    * Wijmo Library 5.20251.34
    * https://developer.mescius.com/wijmo
    *
    * Copyright(c) MESCIUS inc. All rights reserved.
    *
    * Licensed under the End-User License Agreement For MESCIUS Wijmo Software.
    * us.sales@mescius.com
    * https://developer.mescius.com/wijmo/licensing
    *
    */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { isArray, Control, isUndefined, isFunction, Event } from '@mescius/wijmo';
import * as VueModule from 'vue';
///<tsbundler-esm-replace />
var Vue = VueModule.default || VueModule;
///<tsbundler-esm-replace-with />
//declare var Vue: any;
///<tsbundler-esm-replace-end />
export var VueApi = (Vue.version && (Vue.version[0] === '3'))
    ? {
        isV3Plus: true,
        modelValueProp: 'modelValue',
        extend: function (x) { return x; },
        h: Vue.h,
        render: Vue.render,
    }
    : Vue;
// Base Vue component for all Wijmo for Vue component. See WjComponentBehavior docs
// for the details.
var WjVueBaseDefinition = {
    beforeCreate: function () {
        var behClass = this.$options[WjComponentBehavior._behClassProp];
        if (behClass) {
            this[WjComponentBehavior._behProp] = behClass._attach(this);
        }
    },
    mounted: function () {
        this[WjComponentBehavior._behProp].lhMounted();
    },
};
WjVueBaseDefinition[VueApi.isV3Plus ? 'unmounted' : 'destroyed'] = function () {
    this[WjComponentBehavior._behProp].lhDestroyed();
};
var WjVueBase = VueApi.extend(WjVueBaseDefinition);
/*
* Implements a 'behavior' attached to a Vue instance to control its functionality.
* The 'behavior' pattern is used due to limitations in Vue component implementation
* related to component inheritance. We can't represent a component using a conventional
* TS/ES6 class and be able to override base class methods.
* So, the 'behavior' class instance creates a standard Vue component with the component
* specific 'options' object and lifecycle hook methods (like mounted etc). Behavior instance
* attaches itself to the Vue component, and component calls lifecycle hook methods of the
* behavior (these method names are prefixed with 'lh', e.g. lhMounted).
* This way, the behavior has an access to the component properties/methods via the
* 'component' property, and allows us to override basic behavior methods (both lifecycle
* like lhMounted() and custom like _initParent()).
* The Vue component instance maintained by the behavior extends (in terms of the Vue component
* extension) the base WjVueBase component, which implements the basic lifecycle method calls
* delegation to the behavior's 'lh' methods.
*
* Every Wijmo Vue component is represented by a separate class derived from
* WjComponentBehavior.
* Component behavior class should define its metadata which drives the behavior functionality
* in the static properties,
* the list of possible properties is described in the 'METADATA properties' section
* in this class definition.
* Component behavior classes can override some base class methods like _initParent()
* to customize their functionality.
* IMPORTANT note for the implementer of this and derived classes. The metadata is
* provided using *static* inheritance, i.e. metadata related property values are statically
* overridden in the inherited classes (read constructor functions). Because of this, these static
* properties should be accessed in the class instance methods using the 'this.constructor'
* expression. E.g. it should be 'this.constructor.parentProp', but not
* 'WjComponentBehavior.parentProp'.
*
* Component behavior class creates a Vue component instance using the static
* register() method, that internally calls the Vue.component(...) method
* to globally register the corresponding Vue component. The component export
* pattern looks as follows:
*
* class WjFlexGridBehavior extends WjComponentBehavior {
*    static tag = 'wj-flex-grid';
*    static template = '<div><slot/></div>';
*    static className = 'wijmo.grid.FlexGrid';
* }
* // DOCUMENTATION comment goes here
* export var WjFlexGrid = WjFlexGridBehavior.register();
*
* So we export a variable containing the result of the WjFlexGridBehavior.register()
* call, which is in turn a result of the Vue.component(...) call.
* The behavior class itself is used internally and is not exported.
*
*/
var WjComponentBehavior = /** @class */ (function () {
    function WjComponentBehavior(component) {
        this._isMounted = false;
        this._mountedCBs = [];
        this.component = component;
    }
    WjComponentBehavior._attach = function (component) {
        return new this(component);
    };
    /*
    * Creates and globally registers a corresponding Vue component, and returns the
    * reference to it. This class is used to create Wijmo for Vue component to export
    * for users' consumption. E.g.:
    * export var WjFlexGrid = WjFlexGridBehavior.register();
    */
    WjComponentBehavior.register = function () {
        var _a;
        var options = (_a = {
                data: this.data,
                extends: WjVueBase,
                render: this.render,
                //props: _getProps(this.className, this.extraProps),
                props: this._getProps(),
                model: this._getModel()
            },
            _a[WjComponentBehavior._behClassProp] = this,
            _a);
        return VueApi.isV3Plus
            ? __assign({}, options, { emits: this._getEmits() }) : VueApi.component(this.tag, options);
    };
    // ======== Vue lifecicle hooks. =================
    // Called by the attached Vue "component"'s lifecycle hooks.
    //lhCreated() {
    //}
    // Vue 'mounted' hook.
    WjComponentBehavior.prototype.lhMounted = function () {
        var _this = this;
        //console.log('Base behavior mounted');
        if (this._isChild()) {
            var parCmp = this.component.$parent;
            if (parCmp) {
                this.parent = parCmp[WjComponentBehavior._behProp];
                this.parent._mountedCB(function () {
                    //this._setParent(parCmp);
                    _this._prepareControl();
                    _this._initParent();
                });
            }
        }
        else {
            this._prepareControl();
        }
    };
    WjComponentBehavior.prototype.lhDestroyed = function () {
        if (this._siblingInsertedMO) {
            // Tells the observer to stop watching for other mutations   
            this._siblingInsertedMO.disconnect();
        }
        var control = this.control;
        if (control) {
            if (this._isChild()) {
                var parProp = this._getParentProp();
                if (parProp) {
                    var parControl = this.parent.control, parValue = parControl[parProp];
                    if (isArray(parValue)) {
                        var idx = parValue.indexOf(control);
                        if (idx > -1) {
                            parValue.splice(idx, 1);
                        }
                    }
                }
            }
            else if (control instanceof Control) {
                control.dispose();
            }
        }
    };
    // === End fo Vue lifecicle hooks
    WjComponentBehavior._getProps = function () {
        var ret = [];
        if (this.props) {
            ret = this.props;
        }
        if (this.events) {
            ret = ret.concat(this.events);
        }
        if (this.changeEvents) {
            ret = ret.concat(Object.keys(this.changeEvents));
        }
        if (VueApi.isV3Plus && this.modelProp) {
            ret.push(VueApi.modelValueProp);
        }
        return ret;
    };
    WjComponentBehavior._getModel = function () {
        var modelProp = this.modelProp;
        if (modelProp) {
            return {
                prop: modelProp,
                event: 'update:' + modelProp
            };
        }
        // if (modelProp) {
        //     let modelEvent = this._modelEvent;
        //     if (!modelEvent) {
        //         let changeEvents = this.changeEvents,
        //             eventNames = Object.keys(changeEvents);
        //         for (let evName of eventNames) {
        //             let props = changeEvents[evName];
        //             if (props) {
        //                 let idx = props.indexOf(modelProp);
        //                 if (idx > -1) {
        //                     modelEvent = this._modelEvent = evName;
        //                 }
        //             }
        //         }
        //     }
        //     if (modelEvent) {
        //         return {
        //             prop: modelProp,
        //             event: modelEvent
        //         }
        //     }
        // }
        return null;
    };
    WjComponentBehavior._getEmits = function () {
        var _this = this;
        if (!this.changeEvents) {
            return [];
        }
        var changedProps = [];
        Object.keys(this.changeEvents).forEach(function (event) { return _this.changeEvents[event].forEach(function (prop) {
            if (changedProps.indexOf(prop) < 0) {
                changedProps.push(prop);
            }
        }); });
        if (this.modelProp) {
            changedProps.push(VueApi.modelValueProp);
        }
        return changedProps.map(function (item) { return "update:" + item; });
    };
    // Creates a control instance owned by the directive (analogue of Ng1 _initControl).
    // Can be overridden in the derived class.
    WjComponentBehavior.prototype._createControl = function () {
        //return this.directive._initControl(this._parentInCtor() ? this.parent.control : this.directiveTemplateElement[0]);
        var param = this._isChild() ? (this._isParentInCtor() ? this.parent.control : undefined)
            : this._getElement(), control = new (this.constructor._getControlType())(param);
        return control;
    };
    // For child component, initializes its parent after component's 'control' has been created.
    // Can be overridden in the derived class with the mandatory 'super' call.
    WjComponentBehavior.prototype._initParent = function () {
        var parProp = this._getParentProp();
        if (parProp) {
            var parControl = this.parent.control, parArr = parControl[parProp];
            if (isArray(parArr)) {
                //parValue.push(this.control);
                // insert child at correct index, which is the same as an index of the directive element amid sibling directives
                // of the same type
                var linkIdx = this._getSiblingIndex();
                if (linkIdx < 0 || linkIdx >= parArr.length) {
                    linkIdx = parArr.length;
                }
                parArr.splice(linkIdx, 0, this.control);
                // Set up MutationObserver to handle added nodes
                var targetElement = this._getElement();
                this._siblingInsertedMO = new MutationObserver(this._siblingInserted.bind(this));
                this._siblingInsertedMO.observe(targetElement, { childList: true });
            }
            else {
                parControl[parProp] = this.control;
            }
        }
    };
    // Called on every property update.
    // Can be overridden in the derived class with the 'super' call in case of unprocessed property.
    WjComponentBehavior.prototype._updateControl = function (property, newValue) {
        this.control[property] = newValue;
    };
    // Creates a control and initializes its properties.
    WjComponentBehavior.prototype._prepareControl = function () {
        // instantiate the control
        var control = this.control = this._createControl(), host = this._getElement(), CB = this.constructor;
        // expose reference to the underlying control via the component's
        // 'control' property.
        this.component.control = control;
        // define sibling id
        if (!this._siblingId) {
            if (CB.siblingId == null) {
                CB.siblingId = (++CB._siblingDirId) + '';
            }
            this._siblingId = CB.siblingId;
        }
        host.setAttribute(CB._typeSiblingIdAttr, this._siblingId);
        this._isMounted = true;
        // Notify children before initializing own control's properties
        // Call 'mounted' callbacks
        var cbs = this._mountedCBs;
        this._mountedCBs = [];
        for (var _i = 0, cbs_1 = cbs; _i < cbs_1.length; _i++) {
            var cb = cbs_1[_i];
            cb();
        }
        // Init after children have been initialized and performed necessary actions on this parent.
        _initialize(this);
    };
    WjComponentBehavior.prototype._isChild = function () {
        var ctor = this.constructor;
        return ctor.parentProp != null || ctor.parentInCtor != null;
    };
    WjComponentBehavior.prototype._isParentInCtor = function () {
        return this.constructor.parentInCtor === true;
    };
    WjComponentBehavior.prototype._getParentProp = function () {
        return (VueApi.isV3Plus ? this.component.$props : this.component.$options.propsData).wjProperty || this.constructor.parentProp;
    };
    // Gets an index of this directive host element among another host elements pertain to the same directive type.
    WjComponentBehavior.prototype._getSiblingIndex = function () {
        var thisEl = this._getElement(), parEl = thisEl.parentElement;
        // If parentElement is null, e.g. because this element is temporary in DocumentFragment, the index
        // of the element isn't relevant to the item's position in the array, so we return -1 and thus force
        // a calling code to not reposition the item in the array at all.  
        if (!parEl || !parEl.getAttribute(WjComponentBehavior._typeSiblingIdAttr)) {
            return -1;
        }
        var siblings = parEl.childNodes, idx = -1, dirId = this._siblingId;
        for (var i = 0; i < siblings.length; i++) {
            var curEl = siblings[i];
            if (curEl.nodeType == 1 && curEl.getAttribute(WjComponentBehavior._typeSiblingIdAttr) == dirId) {
                ++idx;
                if (curEl === thisEl) {
                    return idx;
                }
            }
        }
        return -1;
    };
    WjComponentBehavior.prototype._siblingInserted = function (mutationsList) {
        var _this = this;
        for (var _i = 0, mutationsList_1 = mutationsList; _i < mutationsList_1.length; _i++) {
            var mutation = mutationsList_1[_i];
            if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                var addedNodes = Array.from(mutation.addedNodes);
                if (addedNodes.some(function (node) { return node === _this._getElement(); })) {
                    var lIdx = this._getSiblingIndex();
                    var control = this.control;
                    var parArr = this.parent.control[this._getParentProp()];
                    var arrIdx = parArr.indexOf(control);
                    if (lIdx >= 0 && arrIdx >= 0 && lIdx !== arrIdx) {
                        parArr.splice(arrIdx, 1);
                        var newIdx = Math.min(lIdx, parArr.length);
                        parArr.splice(newIdx, 0, control);
                    }
                }
            }
        }
    };
    WjComponentBehavior.prototype._getElement = function () {
        return this.component.$el;
    };
    WjComponentBehavior._getControlType = function () {
        return this.classCtor();
        //if (!this._controlType) {
        //    let cls: any = window,
        //        ns = this.className.split('.');
        //    for (var i = 0; i < ns.length && cls != null; i++) {
        //        cls = cls[ns[i]];
        //    }
        //    this._controlType = cls;
        //}
        //return this._controlType;
    };
    WjComponentBehavior.prototype._mountedCB = function (cb) {
        if (this._isMounted) {
            cb();
        }
        else {
            this._mountedCBs.push(cb);
        }
    };
    // For rendering Component HTML.
    WjComponentBehavior.render = function (createElement) {
        var defaultSlot = this.$slots.default;
        return VueApi.isV3Plus
            ? VueApi.h('div', {}, [defaultSlot && defaultSlot()])
            : createElement('div', [defaultSlot]);
    };
    // additional custom interop level properties
    //static extraProps: string[];
    // ========= End of METADATA properties =====================================================
    WjComponentBehavior._typeSiblingIdAttr = '_wjSiblingId';
    WjComponentBehavior._behClassProp = '_wjBehCl';
    WjComponentBehavior._behProp = '__wjBeh';
    // Name of property containing propertyName => property initialization index map.
    // This is a static property (ComponentBehaviourClass.constructor.__propInitIdx),
    // one map per component class.
    WjComponentBehavior._propIdxMapProp = '__propInitIdx';
    //private static _controlType: ObjectConstructor = null;
    WjComponentBehavior._siblingDirId = 0;
    return WjComponentBehavior;
}());
export { WjComponentBehavior };
// // get an array with a control's properties and events
// export function _getProps(ctlClass: string, extraProps?: string[]) {
//     // resolve control class (in case the module hasn't been loaded)
//     var cls: any = window,
//         ns = ctlClass.split('.');
//     for (var i = 0; i < ns.length && cls != null; i++) {
//         cls = cls[ns[i]];
//     }
//     if (!cls) return null;
//     // start with 'special' members
//     var p = ['control', 'initialized', 'wjProperty',
//         // PATCH: for ComboBox and derived classes, will be eliminated after 
//         // we switch to the metadata based implementation
//         'formatItem'
//     ];
//     // add properties and events on this class and all ancestors
//     for (var proto = cls.prototype; proto != Object.prototype; proto = Object.getPrototypeOf(proto)) {
//         var props = Object.getOwnPropertyNames(proto);
//         for (var i = 0; i < props.length; i++) {
//             var prop = props[i],
//                 pd = Object.getOwnPropertyDescriptor(proto, prop),
//                 eventRaiser = prop.match(/^on[A-Z]/);
//             if (pd.set || eventRaiser) {
//                 if (eventRaiser) {
//                     prop = prop[2].toLowerCase() + prop.substr(3);
//                 }
//                 if (p.indexOf(prop) < 0 && !prop.match(/disabled|required|style/)) {
//                     p.push(prop);
//                 }
//             }
//         }
//     }
//     // add extra properties
//     if (extraProps) {
//         Array.prototype.push.apply(p, extraProps);
//     }
//     // done
//     return p;
// }
// initialize control properties from component, add watchers to keep the control in sync
export function _initialize(behavior) {
    var component = behavior.component, ctl = behavior.control, ctor = behavior.constructor, propIdxMap = ctor[WjComponentBehavior._propIdxMapProp];
    // build property initialization index map, once per component behavior class
    if (!propIdxMap) {
        propIdxMap = ctor[WjComponentBehavior._propIdxMapProp] = {};
        var propArr = ctor.props;
        if (propArr) {
            for (var i = 0; i < propArr.length; i++) {
                propIdxMap[propArr[i]] = i;
            }
        }
    }
    // build list of sorted property names
    var props = [], events = [], eventsChg = [], changeEvents = ctor.changeEvents || {};
    var componentPropsData = VueApi.isV3Plus
        ? component.$props
        : component.$options.propsData;
    var modelProp = ctor.modelProp;
    // for (var prop in component.$options.propsData) {
    for (var prop in componentPropsData) {
        if (propIdxMap[prop] != null) {
            props.push(prop);
        }
        else if (changeEvents[prop]) {
            eventsChg.push(prop);
        }
        else {
            events.push(prop);
        }
    }
    if (VueApi.isV3Plus && modelProp) {
        if (componentPropsData.hasOwnProperty(VueApi.modelValueProp) && !componentPropsData.hasOwnProperty(modelProp)) {
            props.push(modelProp);
        }
    }
    props.sort(function (a, b) { return propIdxMap[a] - propIdxMap[b]; });
    // initialize properties (before setting up event handlers)
    var cmpClass = component[WjComponentBehavior._behProp].constructor, extraProps = cmpClass.extraProps;
    // props.forEach((prop) => {
    //     if ((prop in ctl || extraProps && extraProps.indexOf(prop) > -1) &&
    //             !(ctl[prop] instanceof Event) && !isUndefined(component[prop])) {
    //         //ctl[prop] = component[prop];
    //         component[WjComponentBehavior._behProp]._updateControl(prop, component[prop]);
    //         //component.$watch(prop, _updateControl.bind({ ctl: ctl, prop: prop }));
    //         component.$watch(prop, _updateControl.bind({ cmp: component, prop: prop }));
    //     }
    // });
    function _subscribeEvents(evArr) {
        evArr.forEach(function (ev) {
            // we don't support property change events yet, so no reason to subscribe to parent events
            if (ev !== 'initialized' && ev.indexOf('.') < 0) {
                if (isFunction(component[ev])) {
                    ctl[ev].addHandler(component[ev], ctl);
                }
            }
        });
    }
    // hook up non-change event handlers (before assigning properties)
    _subscribeEvents(events);
    // events.forEach((ev) => {
    //     // we don't support property change events yet, so no reason to subscribe to parent events
    //     if (ev !== 'initialized' && ev.indexOf('.') < 0) {
    //         if (isFunction(component[ev])) {
    //             ctl[ev].addHandler(component[ev], ctl);
    //         }
    //     }
    // });
    props.forEach(function (prop) {
        if (VueApi.isV3Plus) {
            var v3ModelValueProp = VueApi.modelValueProp;
            if (!isUndefined(component[prop])) {
                component[WjComponentBehavior._behProp]._updateControl(prop, component[prop]);
            }
            else if ((prop === modelProp) && !isUndefined(component[v3ModelValueProp])) {
                component[WjComponentBehavior._behProp]._updateControl(prop, component[v3ModelValueProp]);
                component.$watch(v3ModelValueProp, _updateControl.bind({ cmp: component, prop: modelProp }));
            }
            component.$watch(prop, _updateControl.bind({ cmp: component, prop: prop }));
        }
        else {
            if (!isUndefined(component[prop])) {
                component[WjComponentBehavior._behProp]._updateControl(prop, component[prop]);
            }
            component.$watch(prop, _updateControl.bind({ cmp: component, prop: prop }));
        }
    });
    function _updateControl(newValue) {
        //this.ctl[this.prop] = newValue;
        this.cmp[WjComponentBehavior._behProp]._updateControl(this.prop, newValue);
    }
    // style prop handler
    _updateStyleProp();
    function _updateStyleProp() {
        var ele = component.$el, style = {};
        if ('style' in ctl && ele.style.cssText.length) {
            ele.style.cssText.split(';').forEach(function (prop) {
                var kv = prop.split(':');
                if (kv.length == 2) {
                    style[kv[0].trim()] = kv[1].trim();
                }
            });
            ctl['style'] = style;
        }
    }
    // hook up event handlers (after assigning properties)
    _subscribeEvents(eventsChg);
    // // hook up event handlers
    // events.forEach((ev) => {
    //     // we don't support property change events yet, so no reason to subscribe to parent events
    //     if (ev !== 'initialized' && ev.indexOf('.') < 0) {
    //         if (isFunction(component[ev])) {
    //             ctl[ev].addHandler(component[ev], ctl);
    //         }
    //     }
    // });
    // Deprecate this. This was never publicly announced, and it doesn't work
    // correctly with child components (assigns control reference to the parent 
    // component's property (in terms of element tree) instead of a "host" component).
    // // set 'control' pseudo-property so it's accessible to parent component
    // if (component.control && component.$parent) {
    //     component.$parent[component.control] = ctl;
    // }
    // subscribe to update events to generate Vue update events
    if (changeEvents) {
        var eventNames = Object.keys(changeEvents);
        for (var _i = 0, eventNames_1 = eventNames; _i < eventNames_1.length; _i++) {
            var evName = eventNames_1[_i];
            _subscribeChangeEvents(evName, changeEvents[evName], ctl, component);
        }
    }
    function _subscribeChangeEvents(eventName, changeProps, ctl, component) {
        if (changeProps) {
            var ctlEv = ctl[eventName];
            if (ctlEv instanceof Event) {
                ctlEv.addHandler(function (s, e) {
                    for (var _i = 0, changeProps_1 = changeProps; _i < changeProps_1.length; _i++) {
                        var chgProp = changeProps_1[_i];
                        component.$emit('update:' + chgProp, s[chgProp]);
                        if (VueApi.isV3Plus && (chgProp === modelProp) && isUndefined(component[chgProp])) {
                            var v3ModelValueProp = VueApi.modelValueProp;
                            if (!isUndefined(component[v3ModelValueProp])) {
                                component.$emit("update:" + v3ModelValueProp, s[chgProp]);
                            }
                        }
                    }
                }, ctl);
            }
        }
    }
    // invoke 'initialized' event
    if (isFunction(component.initialized)) {
        component.initialized(ctl);
    }
    // done, return a reference to the control
    return ctl;
}
// Entry file. All real code files should be re-exported from here.
// import { _registerModule } from '@mescius/wijmo';
// import * as selfModule from './index';
// _registerModule('wijmo.vue2.base', selfModule);
